import { IoMdClose } from '@react-icons/all-files/io/IoMdClose';
import { motion } from 'framer-motion';
import { navigate } from 'gatsby';
import type { FC } from 'react';
import { useState } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { toast } from 'react-toastify';
import { submitContactRequest } from '../../graphql/mutation';
import { useRequestFormProduct } from '../../lib/request-form-context';
import Modal from '../shared/modal';
import ModalWrapper from '../shared/modal/modal-wrapper';

type RequestFormModalProps = {
  show: boolean;
  setShow: (open: boolean) => void;
};

const RequestFormModal: FC<RequestFormModalProps> = ({ show, setShow }) => {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const product = useRequestFormProduct();
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  async function handleSubmit(): Promise<void> {
    try {
      if (!name.trim()) {
        toast.error('Full name cannot be empty');
        return;
      }

      if (!company.trim()) {
        toast.error('Company name cannot be empty');
        return;
      }

      if (!email.trim()) {
        toast.error('Email cannot be empty');
        return;
      }

      if (!emailPattern.test(email)) {
        toast.error('Invalid email address');
        return;
      }

      if (!phone.trim()) {
        toast.error('Phone number cannot be empty');
        return;
      }

      if (!message.trim()) {
        toast.error('Message cannot be empty');
        return;
      }

      const headerLines = [`Company: ${company.trim()}`];
      if (product) {
        headerLines.push(`Product: ${product.productName}`);
        if (product.brochureName) headerLines.push(`Brochure: ${product.brochureName}`);
        if (product.brochureUrl) headerLines.push(`Brochure link: ${product.brochureUrl}`);
      }
      const composedMessage = `${headerLines.join('\n')}\n\n${message.trim()}`;

      const postData = {
        operationName: 'ContactRequestForm',
        variables: {
          data: {
            Name: name.trim(),
            Email: email.trim(),
            Phone: phone,
            Message: composedMessage,
          },
        },
        query: submitContactRequest,
      };
      setLoading(true);

      const response = await fetch(`${process.env.GATSBY_API_URL}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.GATSBY_API_SECRET}`,
        },
        body: JSON.stringify(postData),
      });

      if (!response.ok) {
        setLoading(false);
        toast.error('Something went wrong');
        return;
      }

      await response.json();
      setLoading(false);
      toast.success('Enquiry submitted successfully');
      setShow(false);
      await navigate(`/thank-you?confirm=${response.ok}`);
    } catch {
      toast.error('Something went wrong');
      setLoading(false);
    }
  }

  return (
    <Modal setShowModal={setShow} showModal={show}>
      <ModalWrapper>
        <motion.div
          animate={{ y: 0 }}
          className="relative m-auto w-full max-w-3xl rounded-md border bg-gray-50 p-6 shadow-2xl md:p-10 reqModal"
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Enquire Now</h2>
              <p className="mt-2 text-sm font-light text-gray-600">
                Please provide your details below and we will get back to you
                shortly.
              </p>
            </div>
            <button
              aria-label="Close enquire form"
              className="transition-smooth hover:scale-105"
              onClick={() => {
                setShow(false);
              }}
              type="button"
            >
              <IoMdClose className="text-2xl text-[#DA2801]" />
            </button>
          </div>
          <form
            className="enquire-form flex w-full flex-col gap-6 border bg-white p-6 md:p-8"
            onSubmit={(ev) => {
              ev.preventDefault();
              void handleSubmit();
            }}
          >
            <div className="grid gap-6 md:grid-cols-2">
              <div className="enquire-form__field">
                <label className="enquire-form__label" htmlFor="enquire-full-name">
                  Full Name
                </label>
                <input
                  className="enquire-form__input"
                  id="enquire-full-name"
                  name="fullName"
                  onChange={(ev) => {
                    setName(ev.target.value);
                  }}
                  placeholder="John"
                  type="text"
                  value={name}
                />
              </div>

              <div className="enquire-form__field">
                <label className="enquire-form__label" htmlFor="enquire-company">
                  Company Name
                </label>
                <input
                  className="enquire-form__input"
                  id="enquire-company"
                  name="companyName"
                  onChange={(ev) => {
                    setCompany(ev.target.value);
                  }}
                  placeholder="Doe"
                  type="text"
                  value={company}
                />
              </div>

              <div className="enquire-form__field">
                <label className="enquire-form__label" htmlFor="enquire-email">
                  Email Address
                </label>
                <input
                  className="enquire-form__input"
                  id="enquire-email"
                  name="email"
                  onChange={(ev) => {
                    setEmail(ev.target.value);
                  }}
                  placeholder="john@example.com"
                  type="email"
                  value={email}
                />
              </div>

              <div className="enquire-form__field enquire-form__field--phone">
                <label className="enquire-form__label" htmlFor="enquire-phone">
                  Phone
                </label>
                <PhoneInput
                  defaultCountry="in"
                  inputProps={{ id: 'enquire-phone', name: 'phone' }}
                  onChange={(nextPhone) => {
                    setPhone(nextPhone);
                  }}
                  placeholder="8427641925"
                  value={phone}
                />
              </div>
            </div>

            <div className="enquire-form__field">
              <label className="enquire-form__label" htmlFor="enquire-message">
                Message
              </label>
              <textarea
                className="enquire-form__input enquire-form__textarea"
                id="enquire-message"
                name="message"
                onChange={(ev) => {
                  setMessage(ev.target.value);
                }}
                placeholder="How can we help with your precision engineering needs?"
                rows={4}
                value={message}
              />
            </div>

            <button
              className="enquire-form__submit"
              disabled={loading}
              type="submit"
            >
              <span>{loading ? 'PLEASE WAIT' : 'SUBMIT'}</span>
            </button>
          </form>
        </motion.div>
      </ModalWrapper>
    </Modal>
  );
};

export default RequestFormModal;
